import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { MailIcon, RefreshCwIcon, CheckCircleIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { auth } from '../../firebase';
import { sendEmailVerification } from 'firebase/auth';
export const EmailVerificationPage = () => {
  const navigate = useNavigate();
  const {
    currentUser,
    refreshUser,
    userData
  } = useAuth();
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationChecking, setVerificationChecking] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  // PHASE 2 FIX: Enhanced routing logic based on profile completeness
  useEffect(() => {
    if (!currentUser) {
      navigate('/student-login');
      return;
    }
    // If email is verified, route based on onboarding progress
    if (currentUser.emailVerified) {
      console.log('Email verified - checking onboarding status');
      // Check if onboarding is complete
      if (userData?.onboardingComplete) {
        console.log('Onboarding complete - redirecting to dashboard');
        navigate('/student-dashboard');
      } else if (userData?.instrument && userData?.skillLevel) {
        // Has basic profile info but onboarding not marked complete
        console.log('Profile partially complete - redirecting to dashboard');
        navigate('/student-dashboard');
      } else {
        // Need to complete onboarding
        console.log('Onboarding incomplete - redirecting to instrument selection');
        navigate('/instrument-selection');
      }
    }
  }, [currentUser, userData, navigate]);
  // Countdown timer for resending verification
  useEffect(() => {
    if (!canResend && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      setCanResend(true);
    }
  }, [countdown, canResend]);
  // Function to resend verification email
  const handleResendVerification = async () => {
    if (!currentUser || !canResend) return;
    try {
      await sendEmailVerification(currentUser);
      setVerificationSent(true);
      setCanResend(false);
      setCountdown(60);
      // Hide the success message after 5 seconds
      setTimeout(() => {
        setVerificationSent(false);
      }, 5000);
    } catch (error) {
      console.error('Error sending verification email:', error);
    }
  };
  // PHASE 2 FIX: Enhanced verification check with proper routing
  const checkVerification = async () => {
    setVerificationChecking(true);
    try {
      await refreshUser();
      // Force a small delay to ensure state updates
      await new Promise(resolve => setTimeout(resolve, 500));
      if (currentUser?.emailVerified) {
        console.log('Email verified successfully!');
        // Check onboarding status
        if (userData?.onboardingComplete) {
          navigate('/student-dashboard');
        } else if (userData?.instrument && userData?.skillLevel) {
          navigate('/student-dashboard');
        } else {
          navigate('/instrument-selection');
        }
      } else {
        // Show a message that verification is still pending
        setVerificationChecking(false);
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
      setVerificationChecking(false);
    }
  };
  return <div className="w-full min-h-screen bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white font-sans" data-id="element-3329">
      <Navbar data-id="element-3330" />
      <main className="pt-20 pb-16" data-id="element-3331">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-3332">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 shadow-lg text-center" data-id="element-3333">
            <div className="bg-deep-pink/20 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center" data-id="element-3334">
              <MailIcon className="h-10 w-10 text-deep-pink" data-id="element-3335" />
            </div>
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-3336">
              Verify Your Email
            </h1>
            <p className="text-lg text-white/80 mb-8" data-id="element-3337">
              We've sent a verification email to{' '}
              <span className="font-medium text-white" data-id="element-3338">
                {currentUser?.email}
              </span>
              . Please check your inbox and click the verification link to
              continue.
            </p>
            {verificationSent && <div className="bg-green-500/20 border border-green-500/30 text-green-300 px-4 py-3 rounded-lg mb-6 flex items-center justify-center" data-id="element-3339">
                <CheckCircleIcon className="h-5 w-5 mr-2" data-id="element-3340" />
                Verification email resent successfully!
              </div>}
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-8" data-id="element-3341">
              <button onClick={checkVerification} disabled={verificationChecking} className="bg-white text-navy-900 hover:bg-gray-100 font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center min-w-[200px]" data-id="element-3342">
                {verificationChecking ? <>
                    <RefreshCwIcon className="h-5 w-5 mr-2 animate-spin" data-id="element-3343" />
                    Checking...
                  </> : <>
                    <RefreshCwIcon className="h-5 w-5 mr-2" data-id="element-3344" />
                    I've Verified My Email
                  </>}
              </button>
              <button onClick={handleResendVerification} disabled={!canResend} className="bg-transparent hover:bg-white/10 border border-white/30 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center min-w-[200px] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3345">
                <MailIcon className="h-5 w-5 mr-2" data-id="element-3346" />
                {canResend ? 'Resend Email' : `Resend in ${countdown}s`}
              </button>
            </div>
            <div className="text-white/60 text-sm" data-id="element-3347">
              <p className="mb-2" data-id="element-3348">
                If you don't see the email, please check your spam folder.
              </p>
              <p data-id="element-3349">
                Need help?{' '}
                <a href="#" className="text-deep-pink hover:text-pink-400 underline" data-id="element-3350">
                  Contact Support
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-3351" />
    </div>;
};